import * as PIXI from 'pixi.js';
import { StoryManager } from '@/systems';

export class AffectionDisplay {
  private container: PIXI.Container;
  private background: PIXI.Graphics;
  private affectionText: PIXI.Text;
  private hostilityText: PIXI.Text;
  private totalText: PIXI.Text;
  private specialDialogsText: PIXI.Text;
  private progressText: PIXI.Text;
  private storyManager: StoryManager;
  constructor() {
    this.storyManager = StoryManager.getInstance();
    this.container = new PIXI.Container();

    // Create background
    this.background = new PIXI.Graphics();
    this.updateBackground();

    // Create affection text (heart emoji + value)
    this.affectionText = new PIXI.Text('❤️ 0', {
      fontFamily: 'Rosarivo, serif',
      fontSize: 16,
      fill: 0xff6b6b, // Light red for affection
      fontWeight: 'bold',
      padding: 4,
    });
    this.affectionText.resolution = window.devicePixelRatio || 2;
    this.affectionText.x = 15;
    this.affectionText.y = 10;

    // Create hostility text (crossed swords emoji + value)
    this.hostilityText = new PIXI.Text('⚔️ 0', {
      fontFamily: 'Rosarivo, serif',
      fontSize: 16,
      fill: 0xff4757, // Darker red for hostility
      fontWeight: 'bold',
      padding: 4,
    });
    this.hostilityText.resolution = window.devicePixelRatio || 2;
    this.hostilityText.x = 15;
    this.hostilityText.y = 35;

    // Create total text (balance scale emoji + net value)
    this.totalText = new PIXI.Text('⚖️ 0', {
      fontFamily: 'Rosarivo, serif',
      fontSize: 14,
      fill: 0xfeca57, // Gold for total
      fontWeight: 'bold',
      padding: 4,
    });
    this.totalText.resolution = window.devicePixelRatio || 2;
    this.totalText.x = 15;
    this.totalText.y = 60;

    // Create special dialogs text (star emoji + count)
    this.specialDialogsText = new PIXI.Text('✨ 0', {
      fontFamily: 'Rosarivo, serif',
      fontSize: 14,
      fill: 0xf39c12, // Orange for special dialogs
      fontWeight: 'bold',
      padding: 4,
    });
    this.specialDialogsText.resolution = window.devicePixelRatio || 2;
    this.specialDialogsText.x = 15;
    this.specialDialogsText.y = 85;

    // Create progress text
    this.progressText = new PIXI.Text('Progress: 0%', {
      fontFamily: 'Rosarivo, serif',
      fontSize: 12,
      fill: 0x95a5a6, // Gray for progress
      fontWeight: 'normal',
      padding: 4,
    });
    this.progressText.resolution = window.devicePixelRatio || 2;
    this.progressText.x = 15;
    this.progressText.y = 110;

    // Add all elements to container
    this.container.addChild(this.background);
    this.container.addChild(this.affectionText);
    this.container.addChild(this.hostilityText);
    this.container.addChild(this.totalText);
    this.container.addChild(this.specialDialogsText);
    this.container.addChild(this.progressText);

    // Position in top-right corner
    this.updatePosition(window.innerWidth, window.innerHeight);

    // Listen for affection/hostility changes
    this.storyManager.addEventListener('affectionChanged', () => {
      this.updateDisplay();
    });

    this.storyManager.addEventListener('hostilityChanged', () => {
      this.updateDisplay();
    });

    // Also listen for legacy points changes for backward compatibility
    this.storyManager.addEventListener('pointsChanged', () => {
      this.updateDisplay();
    });

    // Listen for special dialog unlocks
    this.storyManager.addEventListener('specialDialogUnlocked', () => {
      this.updateDisplay();
    });

    // Initial update
    this.updateDisplay();
  }

  private updateBackground(): void {
    this.background.clear();
    this.background.beginFill(0x000000, 0.8);
    this.background.drawRoundedRect(0, 0, 180, 135, 10); // Increased height for new elements
    this.background.endFill();

    // Add subtle border
    this.background.lineStyle(2, 0x444444, 0.6);
    this.background.drawRoundedRect(0, 0, 180, 135, 10);
  }

  private updateDisplay(): void {
    const affection = this.storyManager.getAffection();
    const hostility = this.storyManager.getHostility();
    const total = this.storyManager.getTotalPoints();
    const specialDialogCount = this.storyManager.getSpecialDialogCount();

    this.affectionText.text = `❤️ ${affection}`;
    this.hostilityText.text = `⚔️ ${hostility}`;

    // Color-code the total based on whether it's positive or negative
    const totalColor = total > 0 ? 0x48c78e : total < 0 ? 0xff6b6b : 0xfeca57;
    this.totalText.style.fill = totalColor;
    this.totalText.text = `⚖️ ${total >= 0 ? '+' : ''}${total}`;

    // Update special dialogs count
    this.specialDialogsText.text = `✨ ${specialDialogCount}`;

    // Update progress - since we no longer have winning conditions, show current day
    const progressColor = affection >= 10 ? 0x48c78e : affection >= 5 ? 0xfeca57 : 0x95a5a6;
    this.progressText.style.fill = progressColor;
    this.progressText.text = `Day ${this.storyManager.getCurrentDay()}`;
  }

  public updatePosition(screenWidth: number, _screenHeight: number): void {
    // Position in top-right corner with some margin (increased for bigger display)
    this.container.x = screenWidth - 200; // 180 (width) + 20 (margin)
    this.container.y = 20;
  }

  public getContainer(): PIXI.Container {
    return this.container;
  }

  public setVisible(visible: boolean): void {
    this.container.visible = visible;
  }

  public show(): void {
    this.setVisible(true);
  }

  public hide(): void {
    this.setVisible(false);
  }

  public destroy(): void {
    this.storyManager.removeEventListener('affectionChanged', () => {
      this.updateDisplay();
    });
    this.storyManager.removeEventListener('hostilityChanged', () => {
      this.updateDisplay();
    });
    this.storyManager.removeEventListener('pointsChanged', () => {
      this.updateDisplay();
    });
    this.container.destroy();
  }
}
